import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {DataService} from './data.service';

/**
 * Result returned by splitAddressesByFlags() — the bill and ship address lists
 * ready to assign to vm.billToAddress and vm.shipToAddress.
 */
export interface AddressSplitResult {
  billList: any[];
  shipList: any[];
}

/**
 * Fields mapped from an entity record onto the transaction header.
 * The component applies these to vm.item.header after calling mapEntityToHeader().
 */
export interface EntityHeaderMapping {
  entity_name: string;
  entityInput: string;
  entity_id: any;
  contact_id: any;
  term_id: any;
  salesrep_id: any;
  tax_id: any;
}

/**
 * Pricing fields mapped from an entity record onto the transaction header.
 * The component applies these to vm.item.header after calling applyEntityPricing().
 */
export interface EntityPricingMapping {
  /** Only set if entity has a price_level */
  price_level?: any;
  /** Only set if entity has a currency_id */
  currency_id?: any;
}

// ---------------------------------------------------------------------------
// Entity Transaction Context — response shape from GET /api/entity/{id}/transaction-context
// ---------------------------------------------------------------------------

export interface EntityTransactionContext {
  entity: EntitySummary;
  addresses: EntityAddresses;
  primaryContact: EntityContact | null;
  pricing: EntityPricing | null;
  balance: EntityBalance | null;
}

export interface EntitySummary {
  id: number;
  name: string;
  entityType: 'Customer' | 'Vendor' | 'Both';
  paymentTerms: string;
  term_id: string | null;
  currencyCode: string;
  taxCode: string;
  priceTier: string;
  taxable: boolean;
  active: boolean;
  project_revenue_account_id: string | null;
  workorder_revenue_account_id: string | null;
}

export interface EntityAddresses {
  billTo: EntityAddress | null;
  shipTo: EntityAddress | null;
  all: EntityAddress[];
}

export interface EntityAddress {
  id: number;
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isBillTo: boolean;
  isShipTo: boolean;
  isDefault: boolean;
  primary_ship_to: boolean;
}

export interface EntityContact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  primary_billing_contact: boolean;
  primary_shipping_contact: boolean;
}

export interface EntityPricing {
  priceListId: number;
  priceListName: string;
  discountPercent: number;
}

export interface EntityBalance {
  creditLimit: number;
  currentBalance: number;
  availableCredit: number;
  pastDueBalance: number;
  onCreditHold: boolean;
}

/**
 * TransactionEntityService
 *
 * Provides pure data-transformation helpers for entity-related logic in the
 * transaction form. All methods are stateless — no HTTP calls, no notifications,
 * no router access. The component is responsible for:
 *
 *   1. Fetching entity, address, and contact data via tableDataService.
 *   2. Calling the appropriate service method with the fetched data.
 *   3. Applying the returned mapping / split result to vm.*.
 *
 * Also exposes loadEntityContext() which calls the aggregate
 * GET /api/entity/{id}/transaction-context endpoint, replacing 4–5 sequential
 * HTTP calls with a single round trip.
 *
 * Phase 5 of the Invoice Form Refactor — see docs/Invoice Form Refactor.md
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionEntityService {

  constructor(private dataService: DataService) {
  }

  // ---------------------------------------------------------------------------
  // HTTP — aggregate entity context endpoint
  // ---------------------------------------------------------------------------

  /**
   * Fetch all entity data needed by a transaction form in a single round trip.
   * Replaces the 4–5 sequential calls previously made by processEntityData() and
   * loadAndProcessEntityAddresses() in invoice-form.component.ts.
   *
   * @param entityId  The entity ID to load context for
   * @returns         Observable<EntityTransactionContext>
   */
  loadEntityContext(entityId: number | string): Observable<EntityTransactionContext> {
    return this.dataService.getData('entity', `${entityId}/transaction-context`).pipe(
      tap((ctx: any) => console.log('[EntityService] loadEntityContext raw response:', ctx))
    );
  }

  /**
   * Fetch the full active entity list for use in dropdowns.
   *
   * @returns Observable of the raw API response (Data array or plain array)
   */
  loadEntities(params: string = 'state=active&pageNumber=0&pageSize=999999'): Observable<any> {
    return this.dataService.getDataWithParams('entity', params);
  }

  /**
   * Fetch all contacts for an entity.
   *
   * @param entityId  The entity ID whose contacts to load
   * @returns         Observable of the raw API response (Data array or plain array)
   */
  loadEntityContacts(entityId: number | string): Observable<any> {
    return this.dataService.getData('entity_contact', `?entity_id=${entityId}&pageNumber=0&pageSize=999999`);
  }

  /**
   * Fetch all addresses for an entity from the dedicated address endpoint.
   *
   * This is a separate call from loadEntityContext() because the transaction-context
   * aggregate endpoint does not return the full address list needed to populate the
   * bill-to / ship-to dropdowns in the AR/AP invoice form. The monolith fetches these
   * via entity_address/addresses — we preserve that pattern here so the dropdowns
   * receive the same data shape (id, name, address_1, city, billing_address, etc.).
   *
   * @param entityId  The entity ID whose addresses to load
   * @returns         Observable of the raw API response (Data array or plain array)
   */
  loadEntityAddresses(entityId: number | string): Observable<any> {
    return this.dataService.getData('entity_address/addresses', `?entity_id=${entityId}&pageNumber=0&pageSize=999999`);
  }

  // ---------------------------------------------------------------------------
  // Address splitting
  // ---------------------------------------------------------------------------

  /**
   * Normalize various truthy shapes used by the API for boolean flags.
   * Handles: true, 1, '1', 'y', 'yes', 'true', 't'.
   */
  isTruthy(v: any): boolean {
    if (v === true || v === 1 || v === '1') {
      return true;
    }
    const s = (v ?? '').toString().toLowerCase();
    return s === 'y' || s === 'yes' || s === 'true' || s === 't';
  }

  /**
   * Read bill/ship flags from an address record using multiple possible property names.
   * Handles: billing_address, is_billing, bill_to, billing / shipping_address, is_shipping, ship_to, shipping.
   */
  getAddressFlags(address: any): { bill: boolean; ship: boolean } {
    // snake_case fields from entity_address endpoint: is_bill_to, billing_address, is_billing, bill_to, billing
    const bill =
      this.isTruthy(address?.is_bill_to) ||
      this.isTruthy(address?.billing_address) ||
      this.isTruthy(address?.is_billing) ||
      this.isTruthy(address?.bill_to) ||
      this.isTruthy(address?.billing);
    const ship =
      this.isTruthy(address?.is_ship_to) ||
      this.isTruthy(address?.shipping_address) ||
      this.isTruthy(address?.is_shipping) ||
      this.isTruthy(address?.ship_to) ||
      this.isTruthy(address?.shipping);
    return {bill, ship};
  }

  /**
   * Split a flat array of entity addresses into bill-to and ship-to lists.
   *
   * Rules:
   * - If an address has both bill and ship flags → appears in both lists.
   * - If only bill flag → bill list only.
   * - If only ship flag → ship list only.
   * - If neither flag is set → included in BOTH lists (legacy fallback behavior).
   *
   * Replaces: the inline splitting logic inside loadAndProcessEntityAddresses().
   *
   * @param addresses  Flat array of address records from the API
   * @returns          { billList, shipList }
   */
  splitAddressesByFlags(addresses: any[]): AddressSplitResult {
    const billList: any[] = [];
    const shipList: any[] = [];

    if (!Array.isArray(addresses)) {
      return {billList, shipList};
    }

    for (const addr of addresses) {
      const {bill, ship} = this.getAddressFlags(addr);
      if (bill && ship) {
        billList.push(addr);
        shipList.push(addr);
      } else if (bill) {
        billList.push(addr);
      } else if (ship) {
        shipList.push(addr);
      } else {
        // No flags — include in both to preserve legacy behavior
        billList.push(addr);
        shipList.push(addr);
      }
    }

    return {billList, shipList};
  }

  // ---------------------------------------------------------------------------
  // Default address selection
  // ---------------------------------------------------------------------------

  /**
   * Determine whether an address record is marked as primary.
   * Handles: primary_address, is_primary, primary.
   */
  isPrimaryAddress(address: any): boolean {
    return (
      this.isTruthy(address?.is_default) ||
      this.isTruthy(address?.primary_address) ||
      this.isTruthy(address?.is_primary) ||
      this.isTruthy(address?.primary)
    );
  }

  /** Return the id of an address record. */
  private addressId(address: any): any {
    return address?.id ?? null;
  }

  /**
   * Pick the default address from a list:
   * - Returns null for empty lists.
   * - Returns the only item for single-item lists.
   * - Returns the first primary address if one exists.
   * - Falls back to the first item in the list.
   */
  pickDefaultAddress(list: any[]): any | null {
    if (!Array.isArray(list) || list.length === 0) {
      return null;
    }
    if (list.length === 1) {
      return list[0];
    }
    const primary = list.find(a => this.isPrimaryAddress(a));
    return primary ?? list[0];
  }

  /**
   * Resolve the bill-to address ID to set on the header, given:
   * - An optional explicitly preferred address ID (e.g. from an existing document).
   * - Whether this is a new document.
   * - The current header bill_address_id.
   * - The available bill-to address list.
   *
   * Returns the resolved address ID, or null if none can be determined.
   *
   * @param billList              Available bill-to addresses
   * @param isNewDocument         True if this is a new (unsaved) document
   * @param currentBillAddressId  The current header.bill_address_id value
   * @param preferredId           Optional explicitly preferred address ID
   */
  resolveBillAddressId(
    billList: any[],
    isNewDocument: boolean,
    currentBillAddressId: any,
    preferredId?: string
  ): any {
    if (preferredId) {
      const exists = billList.some(a => this.addressId(a) === preferredId);
      return exists ? preferredId : (this.addressId(this.pickDefaultAddress(billList)) ?? null);
    }
    if ((isNewDocument || !currentBillAddressId) && billList.length > 0) {
      return this.addressId(this.pickDefaultAddress(billList)) ?? null;
    }
    // Existing document — validate current selection still exists
    if (!isNewDocument && currentBillAddressId) {
      const exists = billList.some(a => this.addressId(a) === currentBillAddressId);
      return exists ? currentBillAddressId : null;
    }
    return currentBillAddressId ?? null;
  }

  /**
   * Resolve the ship-to address ID to set on the header.
   * Same logic as resolveBillAddressId but for the ship-to list.
   *
   * @param shipList              Available ship-to addresses
   * @param isNewDocument         True if this is a new (unsaved) document
   * @param currentShipAddressId  The current header.ship_address_id value
   * @param preferredId           Optional explicitly preferred address ID
   */
  resolveShipAddressId(
    shipList: any[],
    isNewDocument: boolean,
    currentShipAddressId: any,
    preferredId?: string
  ): any {
    if (preferredId) {
      const exists = shipList.some(a => this.addressId(a) === preferredId);
      return exists ? preferredId : (this.addressId(this.pickDefaultAddress(shipList)) ?? null);
    }
    if ((isNewDocument || !currentShipAddressId) && shipList.length > 0) {
      return this.addressId(this.pickDefaultAddress(shipList)) ?? null;
    }
    if (!isNewDocument && currentShipAddressId) {
      const exists = shipList.some(a => this.addressId(a) === currentShipAddressId);
      return exists ? currentShipAddressId : null;
    }
    return currentShipAddressId ?? null;
  }

  // ---------------------------------------------------------------------------
  // Entity → header field mapping
  // ---------------------------------------------------------------------------

  /**
   * Map the fields from an entity record that belong on the transaction header.
   * The component applies the returned object to vm.item.header.
   *
   * Replaces: the inline field assignments inside processEntityData().
   *
   * @param entity  The entity record from vm.entity
   * @returns       EntityHeaderMapping with all header fields to set
   */
  mapEntityToHeader(entity: any): EntityHeaderMapping {
    return {
      entity_name: entity?.name ?? '',
      entityInput: entity?.name ?? '',
      entity_id: entity?.id ?? null,
      contact_id: entity?.contact_id ?? null,
      // transaction-context endpoint returns payment_terms (string label); legacy entity list uses term_id (id)
      term_id: entity?.term_id ?? entity?.payment_terms ?? null,
      salesrep_id: entity?.salesrep_id ?? null,
      // transaction-context endpoint returns tax_code; legacy uses tax_id
      tax_id: entity?.tax_id ?? entity?.tax_code ?? null,
    };
  }

  // ---------------------------------------------------------------------------
  // Entity pricing mapping
  // ---------------------------------------------------------------------------

  /**
   * Map pricing-related fields from an entity record onto the transaction header.
   * Only fields that are present on the entity are included in the result —
   * the component should only overwrite header fields that are present in the mapping.
   *
   * Replaces: the inline assignments inside updatePricingBasedOnEntity().
   *
   * @param entity  The entity record (or entity ID — the component resolves it first)
   * @returns       EntityPricingMapping with only the fields that should be applied
   */
  applyEntityPricing(entity: any): EntityPricingMapping {
    const mapping: EntityPricingMapping = {};
    if (entity?.price_level) {
      mapping.price_level = entity.price_level;
    }
    if (entity?.currency_id) {
      mapping.currency_id = entity.currency_id;
    }
    return mapping;
  }

  // ---------------------------------------------------------------------------
  // Entity list filtering
  // ---------------------------------------------------------------------------

  /**
   * Filter an entity list to only receivable (customer) entities.
   * Used by spliceReceivableEntities() — returns a new filtered array
   * rather than mutating the original.
   *
   * @param entities         Full entity list
   * @param currentEntityId  The currently selected entity ID (always kept)
   * @returns                Filtered array
   */
  filterReceivableEntities(entities: any[], currentEntityId?: any): any[] {
    if (!Array.isArray(entities)) {
      return [];
    }
    return entities.filter(e => {
      const isReceivable = e.receivable !== '0';
      const isCurrent = currentEntityId && e.id === currentEntityId;
      return isReceivable || isCurrent;
    });
  }

  /**
   * Filter an entity list to only payable (vendor) entities.
   * Used by splicePayableEntities() — returns a new filtered array
   * rather than mutating the original.
   *
   * @param entities         Full entity list
   * @param currentEntityId  The currently selected entity ID (always kept)
   * @returns                Filtered array
   */
  filterPayableEntities(entities: any[], currentEntityId?: any): any[] {
    if (!Array.isArray(entities)) {
      return [];
    }
    return entities.filter(e => {
      const isPayable = e.payable !== '0';
      const isCurrent = currentEntityId && e.id === currentEntityId;
      return isPayable || isCurrent;
    });
  }

  /**
   * Build the entityName display list from a filtered entity array.
   * Returns an array of { name } objects for typeahead / dropdown binding.
   *
   * Replaces: the inline loop inside buildEntityName().
   *
   * @param entities  Filtered entity array
   * @returns         Array of { name: string } objects
   */
  buildEntityNameList(entities: any[]): Array<{ name: string }> {
    if (!Array.isArray(entities)) {
      return [];
    }
    return entities.map(e => ({name: e.name ?? ''}));
  }
}
